function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ENTITLEMENTS_PLATFORM_TYPES } from '@adsk/alloy-provider';
import { Box, Text } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import { LogoAutodeskBim360Icon, LogoAutodeskAccIcon, TriangleUpIcon, TriangleDownIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { Flyout } from '@adsk/alloy-react-tooltip';
import ProjectLoader from './ProjectLoader';
import ProjectPicker from './ProjectPicker';
import { accountsListShape, projectShape } from '../helpers/propTypes';
const StyledCurrentTemplateLabel = styled(Box).withConfig({
  displayName: "ProjectButton__StyledCurrentTemplateLabel",
  componentId: "sc-4n8di5-0"
})(["", ""], {
  [theme.mediaQuery.medium]: {
    maxWidth: 100
  }
});
const StyledProjectAccountLabel = styled(Box).withConfig({
  displayName: "ProjectButton__StyledProjectAccountLabel",
  componentId: "sc-4n8di5-1"
})(["", ""], {
  [theme.mediaQuery.medium]: {
    maxWidth: '100px'
  },
  [theme.mediaQuery.small]: {
    display: 'none'
  }
});
/**
 * Acs ProjectButton
 */
const ProjectButton = _ref => {
  let {
    accounts,
    accountId,
    projectId,
    productId,
    currentProject,
    onAccountClick,
    onProjectClick,
    renderProjectPicker = props => /*#__PURE__*/React.createElement(ProjectPicker, props)
  } = _ref;
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);
  const openMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);
  const toggleMenu = useCallback(() => {
    setMenuOpen(v => !v);
  }, []);
  const currentAccount = accounts && accountId && accounts.find(p => p.account_id === accountId) || undefined;
  const currentTemplate = (currentProject === null || currentProject === void 0 ? void 0 : currentProject.classification) === 'template' ? currentProject : undefined;
  const LogoComponent = !currentTemplate && currentProject ? currentProject.platform === ENTITLEMENTS_PLATFORM_TYPES.ACC ? LogoAutodeskAccIcon : LogoAutodeskBim360Icon : undefined;
  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;
  return currentProject || currentAccount || currentTemplate ? /*#__PURE__*/React.createElement(Flyout, {
    show: menuOpen,
    onHide: closeMenu,
    onShow: openMenu,
    style: {
      maxWidth: 'fit-content'
    },
    offset: 15,
    renderBody: props => /*#__PURE__*/React.createElement(Tooltip.Body, _extends({}, props, {
      padding: 0
    })),
    placement: Flyout.PLACEMENTS.BOTTOM,
    renderTarget: props => /*#__PURE__*/React.createElement(Tooltip.Target, props),
    content: renderProjectPicker({
      currentAccountId: accountId,
      accounts,
      currentProjectId: projectId,
      currentProductId: productId,
      onClose: closeMenu,
      onAccountClick,
      onProjectClick
    })
  }, /*#__PURE__*/React.createElement(BasicButton, {
    onClick: toggleMenu,
    "data-testid": "TopNavigation__projects",
    style: {
      padding: 0,
      justifyContent: 'start',
      color: theme.colors.charcoal900,
      '&:hover': {
        color: theme.colors.charcoal700
      }
    }
  }, !currentTemplate && /*#__PURE__*/React.createElement(Box, {
    display: "inline-block",
    overflow: "hidden",
    flexShrink: 0,
    borderRadius: "4px",
    width: "32px",
    height: "32px"
  }, /*#__PURE__*/React.createElement("img", {
    alt: (currentProject === null || currentProject === void 0 ? void 0 : currentProject.name) || (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.account_display_name),
    src: (currentProject === null || currentProject === void 0 ? void 0 : currentProject.imageUrl) || (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.image_url),
    "data-testid": "project-image",
    width: 32,
    height: 32
  })), currentTemplate && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    typography: "bodyMedium",
    color: "charcoal500",
    marginRight: "12px"
  }, T.translate('PROJECT_TEMPLATE')), /*#__PURE__*/React.createElement(StyledCurrentTemplateLabel, {
    typography: "bodyMediumBold",
    color: "charcoal900",
    marginRight: "8px",
    ellipsis: "single",
    maxWidth: "300px"
  }, currentTemplate === null || currentTemplate === void 0 ? void 0 : currentTemplate.name)), !currentTemplate && currentProject && /*#__PURE__*/React.createElement(StyledProjectAccountLabel, {
    textAlign: "left",
    marginLeft: "15px",
    maxWidth: "300px"
  }, currentAccount && /*#__PURE__*/React.createElement(Box, {
    typography: "caption",
    color: "charcoal700",
    textAlign: "left",
    ellipsis: "single"
  }, currentAccount.account_display_name), /*#__PURE__*/React.createElement(Box, {
    typography: "bodyMediumBold",
    ellipsis: "single"
  }, /*#__PURE__*/React.createElement("span", null, currentProject === null || currentProject === void 0 ? void 0 : currentProject.name))), !currentProject && currentAccount && /*#__PURE__*/React.createElement(StyledProjectAccountLabel, {
    textAlign: "left",
    marginLeft: "15px",
    maxWidth: "300px"
  }, /*#__PURE__*/React.createElement(Box, {
    typography: "bodyMediumBold",
    ellipsis: "single"
  }, /*#__PURE__*/React.createElement("span", null, currentAccount.account_display_name))), LogoComponent && /*#__PURE__*/React.createElement(LogoComponent, {
    size: 16,
    style: {
      margin: "0px 4px -".concat(!currentAccount ? 2 : 13, "px 9px"),
      color: theme.colors.charcoal700,
      [theme.mediaQuery.small]: {
        display: 'none'
      }
    }
  }), /*#__PURE__*/React.createElement(TriangleIconComponent, {
    style: {
      color: theme.colors.charcoal700,
      marginBottom: currentProject && currentAccount ? '-12px' : 0,
      [theme.mediaQuery.small]: {
        margin: '0 4px'
      }
    },
    size: 20
  }))) : /*#__PURE__*/React.createElement(ProjectLoader, null);
};
ProjectButton.displayName = 'ProjectButton';
ProjectButton.propTypes = {
  accounts: accountsListShape,
  accountId: PropTypes.string,
  projectId: PropTypes.string,
  productId: PropTypes.string,
  currentProject: projectShape,
  onProjectClick: PropTypes.func,
  onAccountClick: PropTypes.func,
  renderProjectPicker: PropTypes.func
};
export default ProjectButton;
//# sourceMappingURL=ProjectButton.js.map